import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '../config/api';

// 🐛 Admin strip for user bug reports (09-20). THIS is the v1 notification:
// the new-count in the header, newest first. Backend = adminBugReports /
// adminBugReportStatus in handler.js (admin-auth, src/ChatGame/bugReports.js).
// Report text is attacker-controlled: it renders as TEXT only, never HTML.

const STATUS_COLOR = { new: '#e0c36a', seen: '#9ec5ff', fixed: '#7fd18b', wontfix: '#8a93a3' };

export default function AdminBugReports({ adminFetch, adminKey, onCounts }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    if (!adminKey) return;
    try {
      const res = await adminFetch(`${API_BASE_URL}/api/takeGameStep?adminBugReports=true&days=30`);
      const json = await res.json();
      if (json.success) { setData(json); setError(null); if (onCounts) onCounts(json.counts || {}); }
      else setError(json.error || 'unavailable');
    } catch (err) {
      setError(err.message);
    }
  }, [adminKey, adminFetch, onCounts]);

  useEffect(() => { load(); }, [load]);

  const mark = async (id, status) => {
    try {
      const res = await adminFetch(`${API_BASE_URL}/api/takeGameStep?adminBugReportStatus=true&id=${encodeURIComponent(id)}&status=${encodeURIComponent(status)}`);
      const json = await res.json();
      if (!json.success) { setError(json.error || 'update failed'); return; }
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  if (error) return <div style={{ fontSize: 12.5, color: '#d98a8a' }}>🐛 {error}</div>;
  if (!data) return <div style={{ fontSize: 12.5, color: '#9aa7b8' }}>Loading…</div>;

  const all = data.reports || [];
  const rows = showAll ? all : all.filter((r) => r.status === 'new' || r.status === 'seen');
  const btn = { padding: '2px 8px', fontSize: 11.5, borderRadius: 5, border: '1px solid #39455a', background: '#151d29', color: '#cfd8e6', cursor: 'pointer' };

  return (
    <div style={{ fontSize: 12.5, color: '#cfd8e6' }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
        <strong style={{ color: STATUS_COLOR.new }}>{data.counts?.new || 0} new</strong>
        <span>{data.counts?.seen || 0} seen</span>
        <span>{data.counts?.fixed || 0} fixed</span>
        <span>{data.counts?.wontfix || 0} won't fix</span>
        <button type="button" style={btn} onClick={load}>↻ Refresh</button>
        <button type="button" style={btn} onClick={() => setShowAll((v) => !v)}>{showAll ? 'Open only' : 'Show all'}</button>
      </div>
      {rows.length === 0 && <div style={{ color: '#9aa7b8' }}>Nothing open. 🎉</div>}
      {rows.map((r) => {
        const ctx = r.context || {};
        const open = openId === r.id;
        return (
          <div key={r.id} style={{ borderTop: '1px solid #2a3547', padding: '8px 0' }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
              <span style={{ color: STATUS_COLOR[r.status] || '#cfd8e6', fontWeight: 700 }}>#{r.id} {r.status}</span>
              <span style={{ color: '#9aa7b8' }}>{new Date(r.created_at).toLocaleString()}</span>
              <span>{r.reporter_name || 'visitor'}{r.verified ? '' : ' (unverified)'}</span>
              {r.space_id && (
                <a href={`/game-step?gameId=${encodeURIComponent(r.space_id)}`} target="_blank" rel="noreferrer" style={{ color: '#9ec5ff' }}>
                  {r.space_name || r.space_id.slice(0, 8)}{ctx.room ? ` · ${ctx.room}` : ''}
                </a>
              )}
              {r.contact && <span style={{ color: '#9ec5ff' }}>✉ {r.contact}</span>}
            </div>
            <div style={{ margin: '4px 0 6px', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', color: '#e8ecf3' }}>{r.text}</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {['seen', 'fixed', 'wontfix', 'new'].filter((s) => s !== r.status).map((s) => (
                <button key={s} type="button" style={btn} onClick={() => mark(r.id, s)}>mark {s}</button>
              ))}
              <button type="button" style={btn} onClick={() => setOpenId(open ? null : r.id)}>{open ? 'hide context' : 'context'}</button>
            </div>
            {open && (
              <pre style={{ margin: '6px 0 0', padding: 8, background: '#0f1520', borderRadius: 6, fontSize: 11, overflowX: 'auto', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {JSON.stringify(ctx, null, 2)}
              </pre>
            )}
          </div>
        );
      })}
    </div>
  );
}
